import { defineStore } from 'pinia';

/**
 * Auth store for user authentication and authorization
 * This replaces the Vuex d2Auth module
 */
export const useAuthStore = defineStore('auth', {
  state: () => ({
    profile: null,
    roles: [],
    claims: [],
    currentCompany: null,
    loggedIn: false
  }),
  
  getters: {
    /**
     * Check if user has a specific role
     * @param {string} role - The role to check
     * @returns {boolean} - Whether the user has the role
     */
    hasRole: (state) => (roleName) => {
      if (!state.roles.includes(roleName)) {
        return false;
      }
      return true;
    },

    hasAnyRole: (state) => (roles) => {
      if (!(roles instanceof Array)) {
        return false;
      }

      if (!roles.find(role => state.roles.includes(role))) {
        return false;
      }

      return true;
    },
    
    /**
     * Check if user has the specified claim
     * @param {string} claim - The claim to check
     * @returns {boolean} - Whether the user has the claim
     */
    hasClaim: (state) => (permission) => {
      if (typeof permission !== 'string') {
        return false;
      }

      if (!state.claims.includes(permission)) {
        return false;
      }

      return true;
    },
    
    /**
     * Check if user has all of the specified claims
     * @param {string[]} claims - The claims to check
     * @returns {boolean} - Whether the user has all of the claims
     */
    hasAllClaims: (state) => (permissions) => {
      if (!(permissions instanceof Array)) {
        return false;
      }

      if (!permissions.every(permission => state.claims.includes(permission))) {
        return false;
      }

      return true;
    },
    
    /**
     * Check if user has any of the specified claims
     * @param {string[]} claims - The claims to check
     * @returns {boolean} - Whether the user has any of the claims
     */
    hasAnyClaim: (state) => (permissions) => {
      if (!(permissions instanceof Array)) {
        return false;
      }

      if (!permissions.find(permission => state.claims.includes(permission))) {
        return false;
      }

      return true;
    },
    
    /**
     * Get the first available role
     * @returns {string|null} - The first role or null
     */
    role: (state) => {
      return state.roles.length > 0 ? state.roles[0] : null;
    },
    
    /**
     * Get the user's first name
     * @returns {string} - The user's first name
     */
    firstName: (state) => {
      return state.profile?.firstName || '';
    },
    
    /**
     * Get the user's last name
     * @returns {string} - The user's last name
     */
    lastName: (state) => {
      return state.profile?.lastName || '';
    },
    
    /**
     * Get the user's phone number
     * @returns {string} - The user's phone number
     */
    phoneNumber: (state) => {
      return state.profile?.phoneNumber || '';
    },
    
    /**
     * Get the user's full name
     * @returns {string} - The user's full name
     */
    fullName: (state) => {
      return state.profile?.fullName || '';
    },
    
    /**
     * Get the user's email
     * @returns {string} - The user's email
     */
    email: (state) => {
      return state.profile?.email || '';
    }
  },
  
  actions: {
    /**
     * Set user claims
     * @param {string[]} claims - The claims to set
     */
    setClaims(claims) {
      this.claims = claims;
    },
    
    /**
     * Set user roles
     * @param {string[]} roles - The roles to set
     */
    setRoles(roles) {
      this.roles = roles;
    },
    
    /**
     * Set user profile data
     * @param {object} profile - The profile data to set
     */
    setUserProfile(profile) {
      if (!profile) {
        this.profile = {};
        return;
      }
      
      // Log missing profile fields for debugging
      if (!profile.email) {
        console.debug('email was missing from user profile.');
      }
      if (!profile.firstName) {
        console.debug('firstName was missing from user profile.');
      }
      if (!profile.lastName) {
        console.debug('lastName was missing from user profile.');
      }
      if (!profile.fullName) {
        console.debug('fullName was missing from user profile.');
      }
      
      this.profile = profile;
      this.loggedIn = true;
    },
    
    /**
     * Set the current company
     * @param {object} company - The company to set
     */
    setCurrentCompany(company) {
      this.currentCompany = company;
    },
    
    /**
     * Set whether the user is authenticated
     * @param {boolean} isAuthenticated - Whether the user is authenticated
     */
    setAuthenticated(isAuthenticated) {
      this.loggedIn = isAuthenticated;
    },
    
    /**
     * Initialize the auth store with data from the server or window globals
     */
    initialize() {
      console.log('Initializing auth store with window data...', {
        hasUser: !!window.user,
        hasRoles: !!window.roles,
        hasClaims: !!window.claims,
        hasCompany: !!window.company,
        loggedIn: window.loggedIn
      });
      
      // If window globals are available, initialize from them
      if (window.user) {
        this.setUserProfile(window.user);
      }
      
      if (window.company) {
        this.setCurrentCompany(window.company);
      }

      if (window.roles) {
        this.setRoles(window.roles);
      }

      if (window.claims) {
        this.setClaims(window.claims);
      }

      if (window.loggedIn !== undefined) {
        this.setAuthenticated(window.loggedIn);
      }
      
      console.log('Auth store initialized:', {
        profile: this.profile,
        roles: this.roles,
        loggedIn: this.loggedIn,
        hasAuthData: !!this.profile
      });
    }
  }
});